import { Button, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

export function LoginPage() {
  const { login, isDemo } = useAuth();
  const navigate = useNavigate();

  const handleDemoLogin = () => {
    login();
    navigate('/', { replace: true });
  };

  return (
    <div style={styles.root}>
      {/* Sol — sade navy brand paneli */}
      <aside style={styles.left}>
        <div style={styles.leftTop}>
          <div style={styles.brandRow}>
            <span style={styles.brandName}>RaporGör</span>
          </div>
          <div style={styles.brandSub}>Sektöre özel finans raporlama</div>
        </div>

        <div style={styles.leftMid}>
          <Title level={1} style={styles.lead}>
            İşletmenin tüm finansal raporları tek bir panelde.
          </Title>
          <Paragraph style={styles.leadBody}>
            Perakende, stok, cari, kasa, banka ve karlılık raporları — sektörünüze özel hazır şablonlarla dakikalar içinde çalışır.
          </Paragraph>

          <ul style={styles.bullets}>
            {[
              '15 hazır perakende rapor şablonu',
              'Agent kurulumu sonrası 5 dakikada aktif',
              'Tier bazlı erişim (Temel / Orta / İleri)',
            ].map((f) => (
              <li key={f} style={styles.bullet}>
                <span style={styles.bulletTick}>—</span> {f}
              </li>
            ))}
          </ul>
        </div>

        <div style={styles.leftFoot}>
          3 abonelik seviyesi · 30 gün ücretsiz deneme · Kurulum ücretsiz
        </div>
      </aside>

      {/* Sağ — sade giriş paneli */}
      <section style={styles.right}>
        <div style={styles.rightInner}>
          <span style={styles.eyebrow}>Canlı Demo</span>
          <Title level={2} style={styles.heading}>Hoş geldin</Title>
          <Paragraph style={styles.subheading}>
            Ürünü bir perakende işletmesi gibi deneyimle. Tüm raporlar senin için önceden dolduruldu — sadece gezin.
          </Paragraph>

          <Button
            type="primary"
            size="large"
            onClick={handleDemoLogin}
            disabled={!isDemo}
            style={styles.cta}
          >
            Demo olarak giriş yap <ArrowRightOutlined style={{ fontSize: 13 }} />
          </Button>

          {!isDemo && (
            <Paragraph style={styles.note}>
              Kendi hesabınla giriş yakında aktif olacak.
            </Paragraph>
          )}

          <div style={styles.disclaimer}>
            <Text strong style={{ color: 'var(--color-text-primary)' }}>Bu canlı bir demo.</Text>
            <span style={{ color: 'var(--color-text-muted)' }}> Rakamlar tamamen uydurma; yazma işlemleri devre dışı.</span>
          </div>
        </div>
      </section>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 1fr)',
    background: 'var(--color-page-bg)',
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '56px 64px',
    background: '#0f172a',
    color: '#ffffff',
  },
  leftTop: {},
  brandRow: { display: 'flex', alignItems: 'center', gap: 12 },
  brandName: {
    fontSize: 20,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    color: '#ffffff',
  },
  brandSub: {
    marginTop: 4,
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: 'rgba(255,255,255,0.55)',
  },
  leftMid: { maxWidth: 520 },
  lead: {
    color: '#ffffff',
    fontSize: 32,
    lineHeight: 1.2,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: '0 0 16px',
  },
  leadBody: {
    color: 'rgba(255,255,255,0.72)',
    fontSize: 14,
    lineHeight: 1.65,
    margin: 0,
  },
  bullets: {
    listStyle: 'none',
    padding: 0,
    marginTop: 28,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  bullet: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  bulletTick: { color: '#b45309', fontWeight: 700 },
  leftFoot: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.45)',
    letterSpacing: 0.3,
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: 16,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px clamp(24px, 6vw, 96px)',
    background: '#ffffff',
  },
  rightInner: { width: '100%', maxWidth: 420 },
  eyebrow: {
    display: 'inline-block',
    padding: '3px 10px',
    background: '#f1f5f9',
    color: '#475569',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    borderRadius: 2,
    marginBottom: 18,
  },
  heading: {
    margin: 0,
    fontSize: 28,
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.01em',
  },
  subheading: {
    color: 'var(--color-text-secondary)',
    fontSize: 14,
    lineHeight: 1.6,
    marginTop: 8,
    marginBottom: 28,
  },
  cta: {
    width: '100%',
    height: 44,
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 4,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  note: { marginTop: 14, color: 'var(--color-text-muted)', fontSize: 12 },
  disclaimer: {
    marginTop: 40,
    padding: '12px 14px',
    borderLeft: '3px solid var(--color-primary)',
    background: 'var(--color-hover-bg)',
    fontSize: 12,
    lineHeight: 1.55,
  },
};
